import { formatPrice } from '../utils/formatPrice';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import PageWrapper from '../components/PageWrapper';
import { CatalogueSchema } from '../components/seo/CatalogueSchema';
import {
  Search, SlidersHorizontal, X, ArrowUpDown, LayoutGrid, List, ChevronLeft, ChevronRight, Loader2,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { t } from '../data/translations';
import { Product, Category, Sector, Subcategory, SUBCATEGORY_LABELS, SECTOR_LABELS } from '../data/products';
import { CatalogueError } from '../components/CatalogueError';
import { productsApi, ProductsQueryParams, transformProduct } from '../api/products';
import ProductCard from '../components/ProductCard';
import FilterPanel, { FilterState, INITIAL_FILTERS, countActiveFilters } from '../components/FilterPanel';

const DEBOUNCE_MS = 400;
const DEFAULT_LIMIT = 24;

function mapSortToApi(sort: string): { sort_by: string; sort_order: string } {
  switch (sort) {
    case 'priceAsc': return { sort_by: 'price', sort_order: 'asc' };
    case 'priceDesc': return { sort_by: 'price', sort_order: 'desc' };
    case 'nameAz': return { sort_by: 'name', sort_order: 'asc' };
    case 'stock': return { sort_by: 'stock', sort_order: 'desc' };
    default: return { sort_by: 'name', sort_order: 'asc' };
  }
}

function parseUrlParams(): { search: string; filters: FilterState; sortBy: string; page: number } {
  const params = new URLSearchParams(window.location.search);
  
  const search = params.get('q') || '';
  
  const filters: FilterState = { ...INITIAL_FILTERS };
  
  const cat = params.get('category');
  if (cat) filters.categories = [cat as Category];
  
  const sub = params.get('subcategory');
  if (sub) filters.subcategories = [sub];
  
  const sector = params.get('sector');
  if (sector) filters.sectors = [sector];
  
  const brand = params.get('brand');
  if (brand) filters.brands = [brand];
  
  const norm = params.get('norm');
  if (norm) filters.norms = [norm];
  
  const priceMin = params.get('price_min');
  if (priceMin) filters.priceMin = priceMin;
  
  const priceMax = params.get('price_max');
  if (priceMax) filters.priceMax = priceMax;
  
  const stockOnly = params.get('stock_only');
  if (stockOnly === '1') filters.stockOnly = true;
  
  const purchaseMode = params.get('purchase_mode');
  if (purchaseMode) filters.purchaseMode = purchaseMode;
  
  const sortBy = params.get('sort') || 'nameAz';
  const page = parseInt(params.get('page') || '1', 10);
  
  return { search, filters, sortBy, page: Math.max(1, page) };
}

function updateUrl(params: { search?: string; filters?: FilterState; sortBy?: string; page?: number }) {
  const urlParams = new URLSearchParams(window.location.search);
  
  if (params.search !== undefined) {
    if (params.search) {
      urlParams.set('q', params.search);
    } else {
      urlParams.delete('q');
    }
  }
  
  if (params.filters) {
    const f = params.filters;
    if (f.categories.length === 1) urlParams.set('category', f.categories[0]);
    else urlParams.delete('category');
    
    if (f.subcategories.length === 1) urlParams.set('subcategory', f.subcategories[0]);
    else urlParams.delete('subcategory');
    
    if (f.sectors.length === 1) urlParams.set('sector', f.sectors[0]);
    else urlParams.delete('sector');
    
    if (f.brands.length === 1) urlParams.set('brand', f.brands[0]);
    else urlParams.delete('brand');
    
    if (f.norms.length === 1) urlParams.set('norm', f.norms[0]);
    else urlParams.delete('norm');
    
    if (f.priceMin) urlParams.set('price_min', f.priceMin);
    else urlParams.delete('price_min');
    
    if (f.priceMax) urlParams.set('price_max', f.priceMax);
    else urlParams.delete('price_max');
    
    if (f.stockOnly) urlParams.set('stock_only', '1');
    else urlParams.delete('stock_only');
    
    if (f.purchaseMode !== 'all') urlParams.set('purchase_mode', f.purchaseMode);
    else urlParams.delete('purchase_mode');
  }
  
  if (params.sortBy) {
    if (params.sortBy !== 'nameAz') urlParams.set('sort', params.sortBy);
    else urlParams.delete('sort');
  }
  
  if (params.page !== undefined) {
    if (params.page > 1) urlParams.set('page', String(params.page));
    else urlParams.delete('page');
  }
  
  const newUrl = urlParams.toString() ? `?${urlParams.toString()}` : window.location.pathname;
  window.history.replaceState({}, '', newUrl);
}

export default function Catalogue() {
  usePageTitle("Catalogue produits — Lodiss", "Parcourez notre catalogue complet d'EPI, vêtements de travail et équipements professionnels. Disponibilité en temps réel, livraison rapide en Tunisie.");

  const { lang, catalogueFilter, setCatalogueFilter } = useApp();
  const T = t[lang];

  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { search: urlSearch, filters: urlFilters, sortBy: urlSort, page: urlPage } = useMemo(() => parseUrlParams(), []);
  
  const [search, setSearch] = useState(urlSearch);
  const [sortBy, setSortBy] = useState(urlSort);
  const [filters, setFilters] = useState<FilterState>(() => {
    if (catalogueFilter !== 'all') {
      return { ...urlFilters, categories: [catalogueFilter as Category] };
    }
    return urlFilters;
  });
  const [page, setPage] = useState(urlPage);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialLoad = useRef(true);

  const activeCount = countActiveFilters(filters);

  const handleFiltersChange = (f: FilterState) => {
    setFilters(f);
    setPage(1);
    if (f.categories.length === 1) {
      setCatalogueFilter(f.categories[0]);
    } else {
      setCatalogueFilter('all');
    }
    updateUrl({ filters: f, page: 1 });
  };

  const buildQueryParams = useCallback((): ProductsQueryParams => {
    const params: ProductsQueryParams = {
      ...mapSortToApi(sortBy),
      page,
      limit: DEFAULT_LIMIT,
    };

    if (filters.categories.length > 0) {
      params.category = filters.categories[0];
    }
    if (filters.subcategories.length > 0) {
      params.subcategory = filters.subcategories[0];
    }
    if (filters.sectors.length > 0) {
      params.sector = filters.sectors[0];
    }
    if (filters.brands.length > 0) {
      params.brand = filters.brands[0];
    }
    if (filters.norms.length > 0) {
      params.norm = filters.norms[0];
    }
    if (filters.priceMin) {
      params.price_min = parseFloat(filters.priceMin);
    }
    if (filters.priceMax) {
      params.price_max = parseFloat(filters.priceMax);
    }
    if (filters.stockOnly) {
      params.stock_only = true;
    }
    if (filters.purchaseMode !== 'all') {
      params.purchase_mode = filters.purchaseMode;
    }
    if (search.trim()) {
      params.q = search.trim();
    }

    return params;
  }, [filters, search, sortBy, page]);

  useEffect(() => {

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setSearching(true);
    
    searchTimeoutRef.current = setTimeout(() => {
      setLoading(true);
      setError(null);
      
      productsApi.getProducts(buildQueryParams())
        .then(data => {
          setProducts(data.products.map(transformProduct));
          setTotal(data.total);
          setTotalPages(data.total_pages);
        })
        .catch(err => {
          console.error('Failed to fetch products:', err);
          setError('Failed to load products');
        })
        .finally(() => {
          setLoading(false);
          setSearching(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [buildQueryParams]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setSearching(true);
    setPage(1);
    updateUrl({ search: value, page: 1 });
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
    setPage(1);
    updateUrl({ sortBy: value, page: 1 });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    updateUrl({ page: newPage });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    
    productsApi.getProducts(buildQueryParams())
      .then(data => {
        setProducts(data.products.map(transformProduct));
        setTotal(data.total);
        setTotalPages(data.total_pages);
      })
      .catch(err => {
        console.error('Failed to fetch products:', err);
        setError('Failed to load products');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const activeChips = buildChips(filters, lang);

  const removeChip = (type: string, value: string) => {
    const update = (patch: Partial<FilterState>) => handleFiltersChange({ ...filters, ...patch });
    if (type === 'category') update({ categories: filters.categories.filter(c => c !== value) });
    if (type === 'subcategory') update({ subcategories: filters.subcategories.filter(s => s !== value) });
    if (type === 'sector') update({ sectors: filters.sectors.filter(s => s !== value) });
    if (type === 'brand') update({ brands: filters.brands.filter(b => b !== value) });
    if (type === 'norm') update({ norms: filters.norms.filter(n => n !== value) });
    if (type === 'priceMin') update({ priceMin: '' });
    if (type === 'priceMax') update({ priceMax: '' });
    if (type === 'stockOnly') update({ stockOnly: false });
    if (type === 'purchaseMode') update({ purchaseMode: 'all' });
  };

  const resetAll = () => { 
    handleFiltersChange(INITIAL_FILTERS); 
    setSearch(''); 
    setPage(1);
    updateUrl({ search: '', filters: INITIAL_FILTERS, page: 1 });
  };

  return (
    <PageWrapper>
    <main className="bg-white min-h-screen">
      {/* Top toolbar */}
      <div className="border-b border-gray-100 bg-white sticky top-[calc(2.5rem+4rem)] z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={T.common.search}
                value={search}
                onChange={e => handleSearchChange(e.target.value)}
                className="w-full pl-8 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-lodiss-red focus:ring-1 focus:ring-lodiss-red/20 transition-colors"
              />
              {searching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 size={14} className="text-lodiss-red animate-spin" />
                </div>
              )}
              {search && !searching && (
                <button onClick={() => handleSearchChange('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X size={13} />
                </button>
              )}
            </div>

            <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
              <ArrowUpDown size={13} className="text-gray-400" />
              <select
                value={sortBy}
                onChange={e => handleSortChange(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:border-lodiss-red bg-white text-gray-700"
              >
                <option value="nameAz">{T.common.nameAz}</option>
                <option value="priceAsc">{T.common.priceAsc}</option>
                <option value="priceDesc">{T.common.priceDesc}</option>
                <option value="stock">{lang === 'fr' ? 'Stock décroissant' : 'Highest stock'}</option>
              </select>
            </div>

            <div className="hidden sm:flex items-center gap-0.5 border border-gray-200 rounded-lg p-1 flex-shrink-0">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded transition-colors ${viewMode === 'grid' ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <LayoutGrid size={14} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded transition-colors ${viewMode === 'list' ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <List size={14} />
              </button>
            </div>

            <button
              onClick={() => setMobileFiltersOpen(true)}
              className={`lg:hidden flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-colors flex-shrink-0 ${
                activeCount > 0 ? 'border-lodiss-red text-lodiss-red bg-red-50' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <SlidersHorizontal size={14} />
              <span className="hidden xs:inline">{lang === 'fr' ? 'Filtres' : 'Filters'}</span>
              {activeCount > 0 && (
                <span className="bg-lodiss-red text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                  {activeCount}
                </span>
              )}
            </button>

            <p className="ml-auto text-xs text-gray-400 flex-shrink-0 hidden sm:block">
              <span className="font-semibold text-gray-700">{total}</span> {lang === 'fr' ? 'résultat(s)' : 'result(s)'}
            </p>
          </div>

          {/* Active chips */}
          {activeChips.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2.5 pt-2.5 border-t border-gray-50">
              {activeChips.map(chip => (
                <span key={chip.key} className="inline-flex items-center gap-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded-full font-medium transition-colors">
                  {chip.label}
                  <button onClick={() => removeChip(chip.type, chip.value)} className="text-gray-400 hover:text-gray-600">
                    <X size={10} />
                  </button>
                </span>
              ))}
              <button
                onClick={resetAll}
                className="text-xs text-lodiss-red font-semibold hover:underline flex items-center gap-1 px-1"
              >
                <X size={10} />
                {lang === 'fr' ? 'Tout effacer' : 'Clear all'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-7">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-[13rem] bg-white border border-gray-100 rounded-xl p-4 overflow-y-auto max-h-[calc(100vh-14rem)]">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal size={14} className="text-gray-500" />
                  <span className="text-xs font-bold text-gray-600 uppercase tracking-widest">
                    {lang === 'fr' ? 'Filtres' : 'Filters'}
                  </span>
                </div>
                {activeCount > 0 && (
                  <span className="text-[10px] font-bold bg-lodiss-red text-white px-2 py-0.5 rounded-full">
                    {activeCount}
                  </span>
                )}
              </div>
              <FilterPanel
                filters={filters}
                onChange={handleFiltersChange}
                lang={lang}
                productCount={total}
              />
            </div>
          </aside>

          {/* Product grid */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="py-24 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 border-2 border-lodiss-red border-t-transparent rounded-full animate-spin" />
                </div>
                <p className="text-gray-500">{lang === 'fr' ? 'Chargement...' : 'Loading...'}</p>
              </div>
            ) : error ? (
              <CatalogueError onRetry={handleRetry} lang={lang} />
            ) : products.length === 0 ? (
              <div className="py-24 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
                  <Search size={24} className="text-gray-300" />
                </div>
                <p className="text-gray-600 font-medium mb-1">
                  {lang === 'fr' ? 'Aucun produit trouvé.' : 'No products found.'}
                </p>
                <p className="text-gray-400 text-sm mb-5">
                  {lang === 'fr' ? 'Essayez d\'élargir vos filtres ou votre recherche.' : 'Try widening your filters or search.'}
                </p>
                <button onClick={resetAll} className="px-5 py-2.5 bg-lodiss-red hover:bg-lodiss-dark text-white text-sm font-semibold rounded-lg transition-colors">
                  {lang === 'fr' ? 'Réinitialiser les filtres' : 'Reset filters'}
                </button>
              </div>
            ) : (
              <>
                {viewMode === 'list' ? (
                  <div className="space-y-2.5">
                    {products.map(p => (
                      <ListProductCard key={p.id} product={p} lang={lang} />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                    {products.map(p => (
                      <ProductCard key={p.id} product={p} />
                    ))}
                  </div>
                )}
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <button
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                      className="p-2 border border-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (page <= 3) {
                          pageNum = i + 1;
                        } else if (page >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = page - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                              page === pageNum 
                                ? 'bg-lodiss-red text-white' 
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages}
                      className="p-2 border border-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileFiltersOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-80 max-w-[90vw] bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <SlidersHorizontal size={16} />
                <span className="font-semibold text-gray-900">
                  {lang === 'fr' ? 'Filtres' : 'Filters'}
                </span>
                {activeCount > 0 && (
                  <span className="bg-lodiss-red text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{activeCount}</span>
                )}
              </div>
              <button onClick={() => setMobileFiltersOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-50">
                <X size={18} className="text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <FilterPanel filters={filters} onChange={handleFiltersChange} lang={lang} productCount={total} />
            </div>
            <div className="border-t border-gray-100 p-4">
              <button
                onClick={() => setMobileFiltersOpen(false)}
                className="w-full py-3 bg-lodiss-red hover:bg-lodiss-dark text-white font-semibold rounded-lg text-sm transition-colors"
              >
                {lang === 'fr' ? `Voir ${total} résultat(s)` : `View ${total} result(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
    </PageWrapper>
  );
}

function ListProductCard({ product, lang }: { product: Product; lang: 'fr' | 'en' }) {
  const { addToCart, toggleFavorite, favorites, setPage, setSelectedProductId } = useApp();
  const T = t[lang];
  const isQuote = 1 >= product.quoteThreshold;
  const isFav = favorites.includes(product.id);
  const name = lang === 'fr' ? product.name : product.nameEn;

  const stockMeta = () => {
    if (product.stock === 0) return { label: T.bestsellers.outStock, cls: 'text-red-600 bg-red-50' };
    if (product.stock < 20) return { label: T.bestsellers.lowStock, cls: 'text-amber-600 bg-amber-50' };
    return { label: T.bestsellers.inStock, cls: 'text-emerald-600 bg-emerald-50' };
  };
  const sm = stockMeta();

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4 flex items-center gap-4 hover:shadow-sm hover:border-gray-200 transition-all duration-200">
      <div 
        onClick={() => { window.location.href = "/product/" + product.id; }}
        className="w-14 h-14 rounded-lg bg-gray-50 border border-gray-100 flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity flex items-center justify-center overflow-hidden"
      >
        {product.images && product.images.length > 0 ? (
          <img
            src={product.images[0]}
            alt={name}
            className="w-full h-full object-cover"
          />
        ) : (
          <img
            src="/logo-1762256791_-_Modifie.png"
            alt="Lodiss"
            className="w-full h-full object-contain p-2 opacity-30 grayscale"
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">{product.brand}</p>
        <button
          onClick={() => { window.location.href = "/product/" + product.id; }}
          className="text-sm font-semibold text-gray-900 hover:text-lodiss-red transition-colors text-left leading-snug mt-0.5 truncate block"
        >
          {name}
        </button>
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
          {product.subcategory && (
            <span className="text-[10px] text-gray-400 border border-gray-100 px-1.5 py-0.5 rounded">
              {SUBCATEGORY_LABELS[product.subcategory as Subcategory]?.[lang]}
            </span>
          )}
          {product.sector.slice(0, 2).map(s => (
            <span key={s} className="text-[10px] text-gray-400 border border-gray-100 px-1.5 py-0.5 rounded">
              {SECTOR_LABELS[s as Sector]?.[lang]}
            </span>
          ))}
          {product.norm.slice(0, 1).map(n => (
            <span key={n} className="text-[10px] text-gray-400 border border-gray-100 px-1.5 py-0.5 rounded font-mono">{n}</span>
          ))}
        </div>
      </div>

      <div className="flex-shrink-0 flex flex-col items-end gap-2">
        <div className="text-right">
          <div className="text-base font-bold text-gray-950">{formatPrice(product.price)}</div>
          <div className="text-[10px] text-gray-400">{T.common.ht}</div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => toggleFavorite(product.id)}
            className={`w-7 h-7 rounded-lg border flex items-center justify-center transition-colors ${
              isFav ? 'border-lodiss-red/30 bg-red-50 text-lodiss-red' : 'border-gray-200 text-gray-400 hover:text-lodiss-red'
            }`}
          >
            <svg viewBox="0 0 24 24" className="w-3 h-3" fill={isFav ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </button>
          <button
            onClick={() => { addToCart({ product, qty: 1 }); }}
            disabled={product.stock === 0}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${
              product.stock === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : isQuote ? 'bg-amber-500 hover:bg-amber-600 text-white'
              : 'bg-lodiss-red hover:bg-lodiss-dark text-white'
            }`}
          >
            {isQuote ? T.common.requestQuote : T.common.addToCart}
          </button>
        </div>
      </div>
    </div>
  );
}

interface Chip { key: string; type: string; value: string; label: string; }

function buildChips(f: FilterState, lang: 'fr' | 'en'): Chip[] {
  const chips: Chip[] = [];
  const catLabels: Record<string, { fr: string; en: string }> = {
    vetements: { fr: 'Vêtements Pro', en: 'Workwear' },
    epi: { fr: 'EPI & Sécurité', en: 'PPE & Safety' },
    batteries: { fr: 'Batteries Pro', en: 'Pro Batteries' },
  };
  f.categories.forEach(c => chips.push({ key: `cat-${c}`, type: 'category', value: c, label: catLabels[c]?.[lang] ?? c }));
  f.subcategories.forEach(s => chips.push({ key: `sub-${s}`, type: 'subcategory', value: s, label: SUBCATEGORY_LABELS[s as Subcategory]?.[lang] ?? s }));
  f.sectors.forEach(s => chips.push({ key: `sec-${s}`, type: 'sector', value: s, label: SECTOR_LABELS[s as Sector]?.[lang] ?? s }));
  f.brands.forEach(b => chips.push({ key: `br-${b}`, type: 'brand', value: b, label: b }));
  f.norms.forEach(n => chips.push({ key: `nm-${n}`, type: 'norm', value: n, label: n }));
  if (f.priceMin) chips.push({ key: 'priceMin', type: 'priceMin', value: f.priceMin, label: `≥ ${f.priceMin} TND` });
  if (f.priceMax) chips.push({ key: 'priceMax', type: 'priceMax', value: f.priceMax, label: `≤ ${f.priceMax} TND` });
  if (f.stockOnly) chips.push({ key: 'stock', type: 'stockOnly', value: 'true', label: lang === 'fr' ? 'En stock' : 'In stock' });
  if (f.purchaseMode !== 'all') chips.push({
    key: 'mode', type: 'purchaseMode', value: f.purchaseMode,
    label: f.purchaseMode === 'direct' ? (lang === 'fr' ? 'Achat direct' : 'Direct purchase') : (lang === 'fr' ? 'Sur devis' : 'On quote'),
  });
  return chips;
}
